import { useMemo, useState } from 'react';
import { StreamingSettings } from '../../types/database';

// Editor for the desired PICO Business Streaming settings (room-level or
// per-PC override). Produces a PARTIAL settings.json — only fields that are
// not "Unmanaged" are stored and enforced by the agent; everything else keeps
// whatever value the PC currently has. All fields target the `game` profile
// (VR experiences; the `video`/`desktop` profiles are irrelevant to rooms).

type Tri = '' | 'on' | 'off';

interface StreamingForm {
  resolution: string;
  codec: string;
  autoBitrate: Tri;
  bitrateCap: string; // Mbps; written to every tier of the game.bitrate table
  asw: Tri;
  sharpenRate: string;
  superResolution: Tri;
}

const RESOLUTIONS = ['smooth', 'sd', 'hd', 'ultra', 'uhd', 'uhdplus'];
const CODECS = ['h264', 'hevc'];
const BITRATE_TIERS = ['smooth', 'sd', 'hd', 'ultra', 'uhd', 'uhdplus'];

const toTri = (v: unknown): Tri => (v === true ? 'on' : v === false ? 'off' : '');

function settingsToForm(s: StreamingSettings | undefined): StreamingForm {
  const game = (s?.game ?? {}) as Record<string, any>;
  // The bitrate table is per-resolution-tier; the form exposes it as a single
  // cap only when all tiers agree (which is how the form writes it).
  let cap = '';
  if (game.bitrate && typeof game.bitrate === 'object') {
    const vals = Object.values(game.bitrate as Record<string, number>);
    if (vals.length > 0 && vals.every((v) => v === vals[0])) cap = String(vals[0]);
  }
  return {
    resolution: typeof game.resolution === 'string' ? game.resolution : '',
    codec: typeof game.codec === 'string' ? game.codec : '',
    autoBitrate: toTri(game.autoBitrate),
    bitrateCap: cap,
    asw: toTri(game.asw),
    sharpenRate: typeof game.sharpenRate === 'number' ? String(game.sharpenRate) : '',
    superResolution: toTri(game.superResolution),
  };
}

function formToSettings(f: StreamingForm): StreamingSettings {
  const game: Record<string, any> = {};
  if (f.resolution) game.resolution = f.resolution;
  if (f.codec) game.codec = f.codec;
  if (f.autoBitrate) game.autoBitrate = f.autoBitrate === 'on';
  const cap = Number(f.bitrateCap);
  if (f.bitrateCap.trim() !== '' && Number.isFinite(cap) && cap > 0) {
    game.bitrate = Object.fromEntries(BITRATE_TIERS.map((t) => [t, cap]));
  }
  if (f.asw) game.asw = f.asw === 'on';
  const sharpen = Number(f.sharpenRate);
  if (f.sharpenRate.trim() !== '' && Number.isFinite(sharpen)) game.sharpenRate = sharpen;
  if (f.superResolution) game.superResolution = f.superResolution === 'on';
  return Object.keys(game).length > 0 ? { game } : {};
}

// One-line summary of a stored settings object, for the room/PC rows.
export function summarizeSettings(s: StreamingSettings | undefined): string {
  if (!s || Object.keys(s).length === 0) return '';
  const game = (s.game ?? {}) as Record<string, any>;
  const parts: string[] = [];
  if (typeof game.resolution === 'string') parts.push(game.resolution);
  if (typeof game.codec === 'string') parts.push(game.codec);
  if (game.bitrate && typeof game.bitrate === 'object') {
    const vals = Object.values(game.bitrate as Record<string, number>);
    parts.push(vals.length > 0 && vals.every((v) => v === vals[0]) ? `${vals[0]} Mbps` : 'bitrate tiers');
  }
  if (typeof game.autoBitrate === 'boolean') parts.push(`auto-bitrate ${game.autoBitrate ? 'on' : 'off'}`);
  if (typeof game.sharpenRate === 'number') parts.push(`sharpen ${game.sharpenRate}`);
  if (typeof game.asw === 'boolean') parts.push(`frame-interp ${game.asw ? 'on' : 'off'}`);
  if (typeof game.superResolution === 'boolean') parts.push(`superres ${game.superResolution ? 'on' : 'off'}`);
  const extra = Object.keys(s).filter((k) => k !== 'game');
  if (extra.length > 0) parts.push(`+${extra.join(', ')}`);
  return parts.join(' · ') || 'custom';
}

export default function StreamingSettingsForm({
  title,
  initial,
  onSave,
  onClose,
}: {
  title: string;
  initial: StreamingSettings | undefined;
  // null = clear the row (back to unmanaged / inherit)
  onSave: (settings: StreamingSettings | null) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<StreamingForm>(() => settingsToForm(initial));
  const [saving, setSaving] = useState(false);
  const settings = useMemo(() => formToSettings(form), [form]);
  const hadSettings = !!initial && Object.keys(initial).length > 0;
  // Stored settings the form can't represent (hand-edited rows) would be
  // dropped on save — warn instead of silently rewriting them.
  const lossy = useMemo(
    () => hadSettings && JSON.stringify(formToSettings(settingsToForm(initial))) !== JSON.stringify(initial),
    [initial, hadSettings]
  );

  const set = (patch: Partial<StreamingForm>) => setForm((f) => ({ ...f, ...patch }));

  const save = async (value: StreamingSettings | null) => {
    setSaving(true);
    try {
      await onSave(value);
    } finally {
      setSaving(false);
    }
  };

  const triSelect = (label: string, key: 'autoBitrate' | 'asw' | 'superResolution') => (
    <label className="software-bs-field">
      {label}
      <select value={form[key]} onChange={(e) => set({ [key]: e.target.value as Tri })}>
        <option value="">Unmanaged</option>
        <option value="on">On</option>
        <option value="off">Off</option>
      </select>
    </label>
  );

  return (
    <div className="software-bs-editor">
      <div className="software-bs-editor-title">{title}</div>
      <div className="software-bs-fields">
        <label className="software-bs-field">
          Resolution
          <select value={form.resolution} onChange={(e) => set({ resolution: e.target.value })}>
            <option value="">Unmanaged</option>
            {RESOLUTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label className="software-bs-field">
          Codec
          <select value={form.codec} onChange={(e) => set({ codec: e.target.value })}>
            <option value="">Unmanaged</option>
            {CODECS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        {triSelect('Auto bitrate', 'autoBitrate')}
        <label className="software-bs-field">
          Bitrate cap (Mbps)
          <input
            type="number"
            min={1}
            placeholder="Unmanaged"
            value={form.bitrateCap}
            onChange={(e) => set({ bitrateCap: e.target.value })}
          />
        </label>
        <label className="software-bs-field">
          Sharpening (0–100)
          <input
            type="number"
            min={0}
            max={100}
            placeholder="Unmanaged"
            value={form.sharpenRate}
            onChange={(e) => set({ sharpenRate: e.target.value })}
          />
        </label>
        {triSelect('Frame interpolation (ASW, 45fps reprojection)', 'asw')}
        {triSelect('Super resolution', 'superResolution')}
      </div>

      {Object.keys(settings).length > 0 ? (
        <pre className="software-bs-preview">{JSON.stringify(settings, null, 2)}</pre>
      ) : (
        <p className="software-bs-preview-empty">All fields unmanaged — saving clears the stored settings.</p>
      )}
      {lossy && (
        <p className="software-bs-warning">
          The stored settings contain values this form does not cover; saving replaces them with the JSON above.
        </p>
      )}

      <div className="software-actions">
        <button disabled={saving} onClick={() => save(Object.keys(settings).length > 0 ? settings : null)}>
          Save
        </button>
        {hadSettings && (
          <button disabled={saving} onClick={() => save(null)}>
            Clear (unmanage)
          </button>
        )}
        <button disabled={saving} onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
